'use client';

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LoadingScreen } from '@/components/loading';
import { useLoadingStore } from '@/stores/LoadingStore';
import { useTaskStore } from '@/stores/TaskStore';
import { useSettingsStore } from '@/stores/SettingsStore';
import { useTagsStore } from '@/stores/TagsStore';

interface StoreHydratorProps {
  children: React.ReactNode;
}

export const StoreHydrator: React.FC<StoreHydratorProps> = ({ children }) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const { hideLoading, setStoreHydrated } = useLoadingStore();

  useEffect(() => {
    // 记录组件开始时间，确保最少显示时间
    const startTime = Date.now();
    const minDisplayTime = 800; // 800ms 最少显示时间

    // 检查所有使用持久化的 store 是否已经完成水合
    const checkHydration = () => {
      const taskStoreHydrated = useTaskStore.persist.hasHydrated();
      const settingsStoreHydrated = useSettingsStore.persist.hasHydrated();
      const tagsStoreHydrated = useTagsStore.persist.hasHydrated();

      if (taskStoreHydrated && settingsStoreHydrated && tagsStoreHydrated) {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

        setTimeout(() => {
          setStoreHydrated(true);
          hideLoading();

          // 使用 Motion 处理过渡，稍后设置 hydrated
          setTimeout(() => {
            setIsHydrated(true);
          }, 200); // 减少延迟时间
        }, remainingTime);
      }
    };

    // 立即检查一次
    checkHydration();

    // 监听 store 水合完成事件
    const unsubTaskStore = useTaskStore.persist.onFinishHydration(() => {
      checkHydration();
    });

    const unsubSettingsStore = useSettingsStore.persist.onFinishHydration(() => {
      checkHydration();
    });

    const unsubTagsStore = useTagsStore.persist.onFinishHydration(() => {
      checkHydration();
    });

    // 设置超时，防止无限加载
    const timeout = setTimeout(() => {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);

      setTimeout(() => {
        setStoreHydrated(true);
        hideLoading();

        // 使用 Motion 处理过渡，稍后设置 hydrated
        setTimeout(() => {
          setIsHydrated(true);
        }, 200); // 减少延迟时间
      }, remainingTime);
    }, 3000); // 3秒超时

    return () => {
      unsubTaskStore();
      unsubSettingsStore();
      unsubTagsStore();
      clearTimeout(timeout);
    };
  }, [hideLoading, setStoreHydrated]);

  return (
    <div className="fixed inset-0 overflow-hidden">
      <AnimatePresence mode="wait">
        {!isHydrated ? (
          <motion.div
            key="loading"
            className="absolute inset-0"
            initial={{ opacity: 1 }}
            exit={{
              opacity: 0,
              scale: 0.98,
            }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0.0, 0.2, 1]
            }}
          >
            <LoadingScreen />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            className="absolute inset-0 overflow-auto"
            initial={{
              opacity: 0,
              scale: 1.01,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
            transition={{
              duration: 0.4,
              ease: [0.4, 0.0, 0.2, 1],
              delay: 0.05
            }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default StoreHydrator;
